<template>
  <div>
    <validation-observer
      ref="form-repeater-field"
      tag="form"
      class="section content"
    >
      <div class="columns">
        <div class="column has-text-centered">
          <div class="columns validate-tooltip v-center-top v-arrow-center-bottom">
            <transition name="fade">
              <span
                v-if="triggeredOnce && !hasPositions"
                class="column shrink error popover"
                v-text="'You must have at least one position at all times.'"
              />
            </transition>
          </div>
          <h2 class="title is-2">
            Positions
          </h2>
          <div class="subtitle is-2">
            Add/edit current, seeking & past positions
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column is-4">
          <textbox-vee
            id="user-position"
            v-model="newItem.publicPosition"
            placeholder="Position"
            rules="required"
            required
          />
        </div>
        <div class="column is-4">
          <dropdown-vee
            v-model.number="newItem.agencyId"
            :options="agencies"
            name="Agency"
            value-key="id"
            text-key="description"
            default-option="Agency"
            placeholder="Agency"
            rules="required"
            required
          />
        </div>
        <div class="column is-4">
          <dropdown-vee
            v-model.number="newItem.affiliationStatus"
            :options="affiliationStatuses"
            value-key="id"
            text-key="value"
            default-option="Affiliation Status"
            placeholder="Affiliation Status"
            rules="required"
            name="Affiliation Status"
            required
          />
        </div>
      </div>
      <div class="columns is-vcentered">
        <div class="column is-4">
          <textbox-vee
            id="user-profession"
            v-model="newItem.profession"
            placeholder="Profession"
          />
        </div>
        <div class="column is-5">
          <textbox-vee
            id="user-work-address"
            v-model="newItem.workStreetAddress"
            placeholder="Work Address"
          />
        </div>
        <div class="column is-3 has-text-left">
          <checkbox-adv
            id="primary-position"
            v-model="newItem.primary"
            value="Y"
            @change="newItem.primary !== 'Y' ? newItem.primary = 'N' : newItem.primary = 'Y'"
          >
            Primary position
          </checkbox-adv>
        </div>
      </div>
      <div class="columns">
        <div class="column is-3">
          <textbox-vee
            id="user-work-phone"
            v-model="newItem.workPhone"
            placeholder="Work Phone"
            rules="phoneNumber"
            desc="Format ###-###-####"
          />
        </div>
        <div class="column is-3">
          <textbox-vee
            id="user-work-city"
            v-model="newItem.workCity"
            placeholder="City"
          />
        </div>
        <div class="column is-3">
          <dropdown
            v-model.number="newItem.workState"
            :options="states"
            value-key="id"
            text-key="description"
            default-option="State"
            placeholder="State"
          />
        </div>
        <div class="column is-3">
          <textbox-vee
            id="user-work-zip"
            v-model="newItem.workZip"
            rules="zipCode"
            placeholder="Zip Code"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column has-text-centered">
          <button
            id="add-update-positions-btn"
            type="button"
            class="button is-secondary"
            :disabled="updatingTable"
            @click="trigerNewItemAction"
          >
            {{ tableAction }} Position
          </button>
        </div>
      </div>
    </validation-observer>
    <div
      v-if="items"
      class="columns"
    >
      <div class="column">
        <v-client-table
          ref="vc-table"
          :data="allItems"
          :columns="table"
          :options="tableOptions"
        >
          <div
            slot="agencyId"
            slot-scope="props"
          >
            {{ getControlValueDesc(props.row.agencyId, 'agencies') }}
          </div>
          <div
            slot="affiliationStatus"
            slot-scope="props"
          >
            {{ getControlValueDesc(props.row.affiliationStatus, 'affiliationStatuses') }}
          </div>
          <div
            slot="primary"
            slot-scope="props"
          >
            <i
              v-if="props.row.primary === 'Y'"
              class="fad fa-check-square"
            />
          </div>
          <div
            slot="edit"
            slot-scope="props"
          >
            <button
              :id="`edit-position-btn-${props.row.id}`"
              type="button"
              title="Edit"
              aria-label="Edit item"
              class="button is-small is-tertiary has-text-bell-yellow"
              @click.prevent="editItem(props.row.id)"
            >
              <i
                aria-hidden="true"
                class="fa fa-pencil-alt"
              />
            </button>
          </div>
          <div
            slot="delete"
            slot-scope="props"
          >
            <button
              :id="`delete-position-btn-${props.row.id}`"
              type="button"
              title="Delete"
              aria-label="Delete item"
              class="button is-small is-tertiary has-text-danger"
              @click.prevent="deleteItem(props.row.id); triggeredOnce = true;"
            >
              <i
                aria-hidden="true"
                class="fa fa-trash"
              />
            </button>
          </div>
          <div
            slot="clone"
            slot-scope="props"
          >
            <button
              :id="`clone-position-btn-${props.row.id}`"
              type="button"
              title="Clone"
              aria-label="Clone item"
              class="button is-small is-tertiary has-text-primary"
              @click.prevent="cloneItem(props.row.id, [{ key: 'primary', default: 'N' }])"
            >
              <i
                aria-hidden="true"
                class="fa fa-copy"
              />
            </button>
            <br>
          </div>
        </v-client-table>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import { Textbox, Dropdown } from '@phila/phila-ui';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';

import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'Positions',
  components: {
    TextboxVee,
    Dropdown,
    DropdownVee,
    CheckboxAdv,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  props: {
    type: {
      type: String,
      default: 'new-user',
    },
  },
  data() {
    return {
      triggeredOnce: false,
      items: [],
      newItem: {
        publicPosition: '',
        agencyId: '',
        affiliationStatus: '',
        profession: '',
        workStreetAddress: '',
        workCity: '',
        workPhone: '',
        workState: '',
        workZip: '',
        primary: '',
      },
      table: [
        'publicPosition',
        'agencyId',
        'affiliationStatus',
        'profession',
        'primary',
        'edit',
        'clone',
        'delete',
      ],
      tableOptions: {
        filterable: false,
        sortable: [],
        headings: {
          publicPosition: 'Position',
          agencyId: 'Agency',
          affiliationStatus: 'Status',
          primary: 'Primary',
        },
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
          'primary': 'has-text-centered',
        },
        perPage: 50,
        perPageValues: [],
      },
      updatingTable: false,
    };
  },
  computed: {
    affiliationStatuses () {
      return this.$store.getters[ 'admin/activeControlValues' ]('affiliationstatus');
    },
    agencies () {
      let agencies = this.$store.getters[ 'admin/activeControlValues' ]('agency');
      if (agencies) {
        agencies = agencies.sort((a, b) => {
          return a.description > b.description;
        });
      }
      return agencies;
    },
    states () {
      return this.$store.getters[ 'admin/activeControlValues' ]('state');
    },
    hasPositions () {
      //Checks if we have any items at all
      if (this.items.length === 0) {
        return false;
      }

      //If items have no cud defined, they haven't been modified, but exist.
      //If items have cud defined, we need at least one cud of type post or put
      return this.items.some(item => {
        if (!item.cud || (item.cud === 'post' || item.cud === 'put')) {
          return true;
        }
        return false;
      });
    },
    hasPrimaryPosition () {
      return this.items.some(item => {
        if (item.cud !== 'delete' && item.primary === 'Y') {
          return true;
        }
        return false;
      });
    },
  },
  watch: {
    items: {
      handler (newValue) {
        //Convert certain fields to Number
        let toNumItems = [ 'affiliationStatus', 'agencyId', 'workState' ];
        newValue.forEach(item => {
          Object.keys(item).forEach((key) => {
            if (toNumItems.includes(key)) {
              if (item[key] !== null) {
                if (item[key] === "") {
                  item[key] = null;
                } else {
                  item[key] = Number(item[key]);
                }
              }
            }
          });
        });
        this.$emit('positions', newValue);
        this.$emit('hasPositions', this.hasPositions);
        this.$emit('hasPrimaryPosition', this.hasPrimaryPosition);
      },
      deep: true,
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$emit('positions', this.items);
    this.$emit('hasPositions', this.hasPositions);
    this.$emit('hasPositionsReady', true);
    this.$emit('hasPrimaryPosition', this.hasPrimaryPosition);
  },
  methods: {
    getCurrentFilingPositions () {
      if (this.$store.state.filing.currentFilingPositions) {
        return this.cloneObj(this.$store.state.filing.currentFilingPositions);
      }
      return [];
    },
    getUserPositions () {
      if (this.$store.state.user.positions) {
        return this.cloneObj(this.$store.state.user.positions);
      }
      return [];
    },
    getTempUserPositions () {
      if (this.$store.state.user.tempPositions) {
        return this.cloneObj(this.$store.state.user.tempPositions);
      }
      return [];
    },
    async initialAssign () {
      if (this.type === 'user') {
        this.items = await this.getUserPositions();
      } else if (this.type === 'tempUser') {
        this.items = await this.getTempUserPositions();
      } else if (this.type === 'filing') {
        this.items = await this.getCurrentFilingPositions();
      }
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'agency'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'affiliationstatus'));
      if (this.type === 'user') {
        promises.push(this.$store.dispatch('user/fetchUserPositions'));
      } else if (this.type === 'tempUser') {
        promises.push(this.$store.dispatch('user/fetchUserPositions', this.$store.state.user.tempId));
      } else if (this.type === 'filing') {
        promises.push(this.$store.dispatch('filing/fetchCurrentFilingPositions'));
      }
      await Promise.all(promises);
    },
    updatePrimaryPosition () {
      if (this.newItem.primary === 'Y') {
        this.items.forEach(item => {
          Vue.set(item, 'primary', 'N');
          if (item.cud) {
            if (item.cud !== 'post' && item.cud !== 'delete') {
              Vue.set(item, 'cud', 'put');
            }
          } else {
            Vue.set(item, 'cud', 'put');
          }
        });
      }
    },
    async trigerNewItemAction () {
      this.triggeredOnce = true;
      this.updatingTable = true;
      const validPositions = await this.$refs['form-repeater-field'].validate();

      this.updatePrimaryPosition();

      if (!validPositions) {
        this.updatingTable = false;
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      this.$refs['form-repeater-field'].reset();
      this.updatingTable = false;
      this.triggeredOnce = true;
    },
  },
};
</script>